import { type VirtualTableColumn } from "@snapchat/snap-design-system";

import { type TrendingTag } from "../../gql-generated/types";
import { EXPIRING_SOON_THRESHOLD_DAYS, isChallengeTag } from "../utils";

import {
    Cell,
    HeadingText,
    ParagraphText,
    ParagraphTextBold,
    RemainingDaysStatus,
} from "./TrendingTagsListColumns.styles";

interface CellRenderer {
    row: {
        index: number;
    };
    data: Readonly<TrendingTag[]>;
}

const getRenderer = (fn: (tag: TrendingTag, rowIndex: number) => React.JSX.Element) => {
    return ({ row, data }: CellRenderer) => {
        const trendingTag = data[row.index] ?? ({} as TrendingTag);
        return <div role={isChallengeTag(trendingTag.tag) ? "emphasis" : ""}>{fn(trendingTag, row.index)}</div>;
    };
};

const daysBetween = (currentDate: string, expirationDate: string) => {
    const d1 = new Date(currentDate);
    const d2 = new Date(expirationDate);
    return Math.ceil((d2.getTime() - d1.getTime()) / (1000 * 3600 * 24));
};

const hoursBetween = (currentDate: string, expirationDate: string) => {
    const d1 = new Date(currentDate);
    const d2 = new Date(expirationDate);
    return Math.ceil((d2.getTime() - d1.getTime()) / (1000 * 3600));
};

export const getColumns = (): VirtualTableColumn<TrendingTag>[] => {
    return [
        {
            id: "event",
            width: 160,
            Header: () => <Cell>Event</Cell>,
            Cell: getRenderer(({ event }) => <HeadingText>{event}</HeadingText>),
        },
        {
            id: "tag",
            width: 180,
            Header: () => <Cell>Tag</Cell>,
            Cell: getRenderer(({ tag }) => <HeadingText>#{tag}</HeadingText>),
        },
        {
            id: "location",
            Header: () => <Cell>Location</Cell>,
            width: 150,
            Cell: getRenderer(({ location }) => (
                <ParagraphText>
                    {location.graphicUnicode} {location.name}
                </ParagraphText>
            )),
        },
        {
            id: "expiresAt",
            width: 120,
            Header: () => <Cell>Expiration Date</Cell>,
            Cell: getRenderer(({ expiresAt }) => (
                <ParagraphText>{new Date(expiresAt).toLocaleDateString()}</ParagraphText>
            )),
        },
        {
            id: "remainingDays",
            width: 100,
            Cell: getRenderer(({ expiresAt }) => {
                const daysRemaining = daysBetween(new Date().toISOString(), expiresAt);
                const remaingingHours = hoursBetween(new Date().toISOString(), expiresAt);

                const affixLetterDays = daysRemaining > 1 ? "s" : "";
                const affixLetterHours = remaingingHours > 1 ? "s" : "";

                const remainingText =
                    remaingingHours <= 24
                        ? `${remaingingHours} Hour${affixLetterHours}`
                        : `${daysRemaining} Day${affixLetterDays}`;

                if (daysRemaining > EXPIRING_SOON_THRESHOLD_DAYS) {
                    return <></>;
                }

                return (
                    <ParagraphTextBold>
                        <RemainingDaysStatus status={"warning"} />
                        {remainingText} Left
                    </ParagraphTextBold>
                );
            }),
        },
    ];
};
